import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  CashFlowSummary,
  CashTransaction,
  CashTransactionSource,
  CashTransactionType,
  PaginatedCashTransactions,
} from '@/types/cash'
import type { CashTransactionFormData, CashTransferFormData } from '@/validators/cash'

export interface ListCashTransactionParams {
  search?: string
  accountId?: string
  categoryId?: string
  type?: CashTransactionType
  source?: CashTransactionSource
  startDate?: string
  endDate?: string
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListCashTransactionParams) => ['cash-transactions', params] as const,
}

/** Invalida tudo que depende do ledger: lançamentos, saldos e resumo. */
function invalidateCash(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['cash-transactions'] })
  qc.invalidateQueries({ queryKey: ['cash-accounts'] })
  qc.invalidateQueries({ queryKey: ['cash-flow-summary'] })
}

export function useCashTransactions(params: ListCashTransactionParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.get<PaginatedCashTransactions>('/cash-transactions', { params }).then((r) => r.data),
  })
}

export type CashFlowSummaryParams = Omit<ListCashTransactionParams, 'page' | 'perPage'>

export function useCashFlowSummary(params: CashFlowSummaryParams = {}) {
  return useQuery({
    queryKey: ['cash-flow-summary', params],
    queryFn: () =>
      api.get<CashFlowSummary>('/cash-transactions/summary', { params }).then((r) => r.data),
  })
}

export function useCreateCashTransaction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CashTransactionFormData) =>
      api.post<CashTransaction>('/cash-transactions', data).then((r) => r.data),
    onSuccess: () => invalidateCash(qc),
  })
}

export function useCreateTransfer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CashTransferFormData) =>
      api.post('/cash-transactions/transfers', data).then((r) => r.data),
    onSuccess: () => invalidateCash(qc),
  })
}

export function useReverseCashTransaction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post<CashTransaction>(`/cash-transactions/${id}/reverse`, { reason }).then((r) => r.data),
    onSuccess: () => invalidateCash(qc),
  })
}
